// Migrated from /pages/proposals.tsx for Phase 5 feature organization
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/data-table';
import { FileText, Search, Plus, Calendar, DollarSign } from 'lucide-react';

interface Proposal {
  id: number;
  title: string;
  customerId: number;
  status: string | null;
  totalPrice: number | null;
  validUntil: Date | null;
  createdAt: Date | null;
  customer?: {
    name: string;
    email: string | null;
  };
  _count?: {
    items: number;
  };
}

export default function ProposalsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const { data: proposals, isLoading } = useQuery<Proposal[]>({
    queryKey: ['/api/proposals'],
  });

  const filteredProposals =
    proposals?.filter((proposal) => {
      const matchesSearch =
        proposal.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        proposal.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        proposal.customer?.email?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = filterStatus === 'all' || proposal.status === filterStatus;

      return matchesSearch && matchesStatus;
    }) || [];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'expired', label: 'Expired' },
  ];

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'expired':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount: number | null) => {
    if (!amount) return '—';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  const columns = [
    {
      header: 'Proposal',
      accessorKey: 'title',
      cell: ({ row }: any) => (
        <div>
          <Link
            href={`/proposals/${row.original.id}`}
            className="font-medium text-blue-600 hover:text-blue-800"
          >
            {row.original.title || `Proposal #${row.original.id}`}
          </Link>
          <div className="text-sm text-gray-500">ID: {row.original.id}</div>
        </div>
      ),
    },
    {
      header: 'Customer',
      accessorKey: 'customer',
      cell: ({ row }: any) => (
        <div>
          <Link
            href={`/customers/${row.original.customerId}`}
            className="font-medium text-blue-600 hover:text-blue-800"
          >
            {row.original.customer?.name || `Customer #${row.original.customerId}`}
          </Link>
          {row.original.customer?.email && (
            <div className="text-sm text-gray-500">{row.original.customer.email}</div>
          )}
        </div>
      ),
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: ({ row }: any) => (
        <Badge variant="secondary" className={getStatusColor(row.original.status)}>
          {row.original.status || 'Draft'}
        </Badge>
      ),
    },
    {
      header: 'Total Price',
      accessorKey: 'totalPrice',
      cell: ({ row }: any) => (
        <div className="font-medium">{formatCurrency(row.original.totalPrice)}</div>
      ),
    },
    {
      header: 'Items',
      accessorKey: '_count',
      cell: ({ row }: any) => <div className="text-center">{row.original._count?.items || 0}</div>,
    },
    {
      header: 'Valid Until',
      accessorKey: 'validUntil',
      cell: ({ row }: any) => {
        const date = row.original.validUntil;
        if (!date) return '—';

        const validDate = new Date(date);
        const isExpired = validDate < new Date();

        return (
          <div className={isExpired ? 'text-red-600' : 'text-gray-900'}>
            {validDate.toLocaleDateString()}
            {isExpired && <div className="text-xs text-red-500">Expired</div>}
          </div>
        );
      },
    },
    {
      header: 'Created',
      accessorKey: 'createdAt',
      cell: ({ row }: any) => {
        const date = row.original.createdAt;
        return date ? new Date(date).toLocaleDateString() : '—';
      },
    },
  ];

  const stats = {
    total: proposals?.length || 0,
    draft: proposals?.filter((p) => !p.status || p.status === 'draft').length || 0,
    pending: proposals?.filter((p) => p.status === 'pending').length || 0,
    approved: proposals?.filter((p) => p.status === 'approved').length || 0,
    totalValue: proposals?.reduce((sum, p) => sum + (p.totalPrice || 0), 0) || 0,
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <FileText className="h-8 w-8" />
          Proposals
        </h1>
        <p className="text-muted-foreground">
          Create and manage solar installation proposals for your customers
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Proposals</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pending}</div>
            <p className="text-xs text-muted-foreground">{stats.draft} drafts</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Approved</CardTitle>
            <Badge className="h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.approved}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.totalValue)}</div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex flex-col sm:flex-row gap-2 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search proposals..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <Button asChild>
          <Link href="/proposals/new">
            <Plus className="h-4 w-4 mr-2" />
            New Proposal
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Proposals</CardTitle>
          <CardDescription>
            {filteredProposals.length} of {proposals?.length || 0} proposals
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable columns={columns} data={filteredProposals} loading={isLoading} />
        </CardContent>
      </Card>
    </div>
  );
}
